import {
  CheckBadgeIcon,
  Cog6ToothIcon,
  ExclamationCircleIcon,
  MicrophoneIcon,
  PhoneIcon,
  VideoCameraIcon,
  VideoCameraSlashIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/solid";
import OT, { AudioOutputDevice } from "@opentok/client";
import { FC, useEffect, useRef, useState } from 'react';
import routes from "routes";
import styles from "./VideoCallRoom.module.scss";
import { useAppDispatch, useAppSelector } from "customHooks/reduxHooks";
import { delStream, setInfo, setStream } from 'store/slices/infoSlice';
import Button from "components/atoms/Button";
import Modal from "components/organisms/Modal";
import GenericForm from "components/molecules/GenericForm";
import FormHeader from "components/atoms/FormHeader";
import green from 'theme/images/reviews/green.webp'
import yellow from 'theme/images/reviews/yellow.webp'
import red from 'theme/images/reviews/red.webp'
import Input from '../atoms/Input';
import { trpc } from '../../utils/trpc';
import { useNavigate } from 'react-router-dom';
import { raiseException } from '../../utils/raiseException';
import { CloseOutlined, WarningOutlined } from "@ant-design/icons";
import { notification } from "antd";

interface Props {
  token: string;
  appointmentId: string;
  pubOptions: OT.PublisherProperties;
  session: OT.Session;
  outputAudioDeviceList: AudioOutputDevice[];
  inputAudioDeviceList: AudioOutputDevice[];
  inputVideoDeviceList: AudioOutputDevice[];
  audioSource: string;
  user: any;
}

type Rating = 'green' | 'yellow' | 'red' | null;

export const VideoCallRoom: FC<Props> = ({
  appointmentId,
  outputAudioDeviceList,
  inputAudioDeviceList,
  inputVideoDeviceList,
  token,
  pubOptions,
  session,
  audioSource,
  user
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [publisherAudioSource, setpublisherAudioSource] = useState(audioSource);
  const [optionPublisher, setOptionPublisher] = useState(pubOptions);
  const [settingModalIsOpen, setSettingModalIsOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(pubOptions.publishVideo);
  const [activeAudio, setActiveAudio] = useState(pubOptions.publishAudio);
  const [activeVideoFilter, setActiveVideoFilter] = useState(
    pubOptions.videoFilter
  );
  const [publisher, setPublisher] = useState<OT.Publisher>();
  const [callEnded, setCallEnded] = useState(false);
  const [callError, setCallError] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [weakConnectionToast, setWeakConnectionToast] = useState<any>();
  const [rating, setRating] = useState<Rating>(null);
  const [feedback, setFeedback] = useState('');
  const [api, contextHolder] = notification.useNotification();
  const textareaRef: any = useRef(null);
  const streams = useAppSelector((store) => store.info.stream);

  const getPatientLeftFeedback = trpc.mainService.feedback.patientLeftFeedback.useQuery(undefined, {
    enabled: user.role !== 'therapist',
  });
  const sendPatientFeedback = trpc.mainService.feedback.saveFeedback.useMutation();

  useEffect(() => {
    if ((rating === 'yellow' || rating === 'red') && textareaRef.current) {
      textareaRef?.current?.focus();
    }
  }, [rating]);

  const handleRatingClick = (selectedRating: Rating) => {
    setRating(selectedRating);
    if (selectedRating === 'green') {
      setFeedback('');
    }
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedback(e.target.value);
  };

  const handleCloseModal = () => {
    setShowReview(false);
    setRating(null);
    setFeedback('');
  };

  const openNotification = (text: string) => {
    api.open({
      key: 'weak_connection',
      message: null,
      description: (
        <span style={{fontWeight: 400, fontSize: '0.875rem'}}>
          {text}
        </span>
      ),
      duration: null,
      className: `info warn`,
      icon: <WarningOutlined/>,
      closeIcon: <CloseOutlined/>,
      onClose: () => {setWeakConnectionToast(false)}
    });
  };

  const handleSubmit = async () => {
    try {
      const response = await sendPatientFeedback.mutateAsync({
        appointmentId: appointmentId,
        review: rating!,
        feedback: feedback.trim(),
      })
      if (response === 'Feedback create successfully!') {
        if (rating === 'green') {
          const trustpilot_invitation = {
            recipientEmail: user.email,
            recipientName: user.given_name,
            referenceId: appointmentId,
            source: 'InvitationScript',
          };
          // @ts-ignore
          tp('createInvitation', trustpilot_invitation);
        }
        dispatch(
          setInfo({
            text: 'Il feedback è stato inviato con successo!',
            variant: 'confirm',
          })
        );
        handleCloseModal()
        navigate(routes.Chat.path)
      } else {
        dispatch(
          setInfo({
            text: 'Errore nell\'invio del feedback.',
            variant: 'error',
          })
        );
      }
    } catch (e) {
      raiseException(e);
      console.error('Error:', e);
    }
  };

  const RatingIcon = ({
    src,
    alt,
    value,
  }: {
    src: string;
    alt: string;
    value: Rating;
  }) => (
    <div
      className={`${styles.reviewImg} ${rating === value ? styles.selected : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => handleRatingClick(value)}
      onKeyDown={(e) => e.key === 'Enter' && handleRatingClick(value)}
      aria-label={`Select ${alt} rating`}
    >
      <img src={src} alt={alt}/>
    </div>
  );

  const handleError = (error: any) => {
    if (error) {
      if (error.name === "OT_USER_MEDIA_ACCESS_DENIED") {
        setCallError('Non hai dato al browser il permesso di utilizzare webcam e microfono, concedi le autorizazioni e ricarica la pagina per continuare');
      } else if (error.name === "OT_NOT_CONNECTED") {
        setCallError('Connessione al servizio Vonage interrotta.');
      } else if (error.code === 'OT_MEDIA_ERR_NETWORK') {
        setCallError('Rilevato problema di rete.');
      } else if (error.code === 'OT_CREATE_PEER_CONNECTION_FAILED') {
        setCallError('Impossibile creare una connessione tra questo client e l\'abbonato o il Vonage Video API Media Router.');
      } else {
        setCallError("Ops! Qualcosa è andato storto.");
      }
    }
  };

  const initSession = () => {
    // create subscriber
    // This code allows you to subscribe to new streams published by other clients in the session.
    session.on("streamCreated", (event) => {
      dispatch(setStream(event.stream.streamId));

      session.subscribe(
        event.stream,
        "subscriber",
        {
          insertMode: "append",
          width: "100%",
          height: "100%",
          fitMode: "contain",
          showControls: false,
        },
        (err) => {
          handleError(err);
        }
      );

      // fix for safari on ios: if you enter the call when there is only one person, it will trigger full screen on the video on ios. This fix avoids the full screen
      if (streams.length <= 1) {
        session.subscribe(
          event.stream,
          undefined,
          {
            insertMode: "append",
            audioVolume: 0,
            width: "100%",
            height: "100%",
            fitMode: "contain",
            showControls: false,
          },
          (err) => {
            handleError(err);
          }
        );
      }
    });

    session.on("streamDestroyed", (event) => {
      dispatch(delStream(event.stream.streamId));
    });

    const publisher = OT.initPublisher(
      "publisher",
      {
        ...pubOptions,
        insertMode: "append",
        width: "100%",
        height: "100%",
        fitMode: "contain",
        style: {
          buttonDisplayMode: "off"
        },
      },
      (err: any) => {
        handleError(err);
      }
    );

    setPublisher(publisher);

    publisher.on("streamDestroyed", (e) => {
      e.preventDefault();
      session.unpublish(publisher);
    });

    publisher.on({
      accessAllowed: (event: any) => {},
      accessDenied: (event: any) => {},
    });

    session.connect(token, (err: any) => {
      if (err) {
        handleError(err);
      } else {
        session.publish(publisher);

        publisher.on("streamCreated", () => {
          startPublisherMonitoring(publisher, session);
        });
      }
    });
  };

  const startPublisherMonitoring = (publisher: any, session: any) => {
    const publisherStats = { current: null as any };

    const statsInterval = setInterval(() => {
      if (!publisher?.stream?.connection?.connectionId || !session?.connection?.connectionId) return;

      const isCurrentUserPublisher =
        publisher.stream.connection.connectionId === session.connection.connectionId;
      if (!isCurrentUserPublisher) return;

      publisher.getStats((error: any, stats: any) => {
        if (error || !stats?.length) return;

        const current = stats[stats.length - 1].stats;
        const previous = publisherStats.current;

        if (previous) {
          const timeDiffSec = (current.timestamp - previous.timestamp) / 1000;

          const calcBitrate = (currentBytes: number, prevBytes: number) =>
            ((currentBytes - prevBytes) * 8) / timeDiffSec

          const audioBitrate = calcBitrate(current.audio.bytesSent, previous.audio.bytesSent);
          const videoBitrate = calcBitrate(current.video.bytesSent, previous.video.bytesSent);

          const audioPacketsSentDiff =
            current.audio.packetsSent - previous.audio.packetsSent;
          const audioPacketsLostDiff =
            current.audio.packetsLost - previous.audio.packetsLost;
          const videoPacketsSentDiff =
            current.video.packetsSent - previous.video.packetsSent;
          const videoPacketsLostDiff =
            current.video.packetsLost - previous.video.packetsLost;

          const audioLossRatio =
            audioPacketsSentDiff + audioPacketsLostDiff > 0
              ? audioPacketsLostDiff / (audioPacketsSentDiff + audioPacketsLostDiff)
              : 0;

          const videoLossRatio =
            videoPacketsSentDiff + videoPacketsLostDiff > 0
              ? videoPacketsLostDiff / (videoPacketsSentDiff + videoPacketsLostDiff)
              : 0;

          console.log("📡 Publisher Stats →", {
            audioBitrate: Math.round(audioBitrate),
            videoBitrate: Math.round(videoBitrate),
            audioLoss: audioLossRatio.toFixed(3),
            videoLoss: videoLossRatio.toFixed(3),
          });

          if (
            audioLossRatio > 0.05 ||
            videoLossRatio > 0.05 ||
            videoBitrate < 300_000
          ) {
            if (!weakConnectionToast) {
              openNotification(
                "La tua connessione è debole, la qualità del video potrebbe essere bassa."
              );
              setWeakConnectionToast(true)
            }
          } else {
            api.destroy('weak_connection')
            setWeakConnectionToast(false)
          }
        }

        publisherStats.current = current;
      });
    }, 5000);

    session.on("sessionDisconnected", () => {
      clearInterval(statsInterval);
      publisher?.destroy();
    });
  };


  const endCall = () => {
    session.disconnect();
    setCallEnded(true);
    // window.close();
    // navigate('/auth/chat')
  };

  useEffect(() => {
    initSession();
  }, [session]);

  useEffect(() => {
    if (!(getPatientLeftFeedback.isSuccess && getPatientLeftFeedback?.data?.patientLeftFeedback) && callEnded) {
      setShowReview(true)
    }
  }, [callEnded, getPatientLeftFeedback.isSuccess, getPatientLeftFeedback?.data?.patientLeftFeedback]);

  return callEnded || callError ? (
    <div className={styles.callClosed}>
      {showReview &&
        <Modal modal subClassName={styles.reviewModal}>
          <>
            <FormHeader
              title=""
              closeModal={handleCloseModal}
              closeButton
            />
            <>
              <div className={styles.reviewTitle}>Ti è piaciuto il nostro servizio?</div>
              <div className={styles.reviewContainer}>
                <RatingIcon src={red} alt="Bad" value="red"/>
                <RatingIcon src={yellow} alt="Medium" value="yellow"/>
                <RatingIcon src={green} alt="Good" value="green"/>
              </div>
              {(rating === 'yellow' || rating === 'red') && (
                <Input
                  ref={textareaRef}
                  placeholder="Come possiamo migliorare?"
                  textarea
                  value={feedback}
                  name="text"
                  onChange={handleFeedbackChange}
                />
              )}
              {rating && <Button
                variant="primary"
                className={styles.reviewButton}
                onClick={handleSubmit}
                disabled={rating !== 'green' && feedback.trim().length <= 4}
              >
                Invia
              </Button>}
            </>
          </>
        </Modal>}
      <div className={styles.infoCard}>
        <div className={callError ? styles.errorIcon : styles.icon}>
          {callError ? <ExclamationCircleIcon /> : <CheckBadgeIcon />}
        </div>
        <div className={styles.infoText}>
          {callError ? callError : "La seduta è terminata!"}
        </div>
        <div className={styles.infoText}>
          <span>Hai chiuso per sbaglio?</span>{" "}
          <span
            className={styles.infoLink}
            onClick={() => {
              window.location.reload();
            }}
          >
            Riprendi la seduta
          </span>
        </div>
        <div className={styles.wrapperBtn}>
          {callError && (
            <Button onClick={() => window.location.reload()}>
              Torna a pertecipare
            </Button>
          )}
          <Button
            onClick={() => navigate(routes.Chat.path)}
            variant={callError ? "secondary" : "primary"}
          >
            Vai alla chat
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className={styles.videosWrapper}>
      {contextHolder}
      {settingModalIsOpen && (
        <Modal close={() => {
          setSettingModalIsOpen(false);
        }}>
          <GenericForm
            isModal
            className={styles.settingModal}
            title={
              <FormHeader
                closeModal={() => setSettingModalIsOpen(false)}
                title="Impostazioni"
                closeButton
              />
            }
            description="Di solito il nostro sistema riconosce automaticamente gli asset di default, ma se vuoi, puoi modificarli!"
            cta={[
              <div className={styles.selectInput}>
                <span>Videocamera</span>
                <select
                  value={optionPublisher.videoSource as string}
                  className={styles.videoCallSelect}
                  onChange={(e) => {
                    e.preventDefault();
                    setOptionPublisher({
                      ...optionPublisher,
                      videoSource: e.target.value,
                    });
                    publisher?.setVideoSource(e.target.value);
                  }}
                >
                  {inputVideoDeviceList?.map((i, index) => (
                    <option key={index} value={i.deviceId!}>
                      {i.label}
                    </option>
                  ))}
                </select>
              </div>,
              <div className={styles.selectInput}>
                <span>Microfono</span>
                <select
                  value={optionPublisher.audioSource as string}
                  className={styles.videoCallSelect}
                  onChange={(e) => {
                    e.preventDefault();
                    setOptionPublisher({
                      ...optionPublisher,
                      audioSource: e.target.value,
                    });
                    publisher?.setAudioSource(e.target.value);
                  }}
                >
                  {inputAudioDeviceList?.map((i, index) => (
                    <option key={index} value={i.deviceId!}>
                      {i.label}
                    </option>
                  ))}
                </select>
              </div>,
              <div className={styles.selectInput}>
                <span>Altoparlante</span>
                <select
                  value={publisherAudioSource}
                  className={styles.videoCallSelect}
                  onChange={(e) => {
                    e.preventDefault();
                    OT.setAudioOutputDevice(e.target.value);
                    setpublisherAudioSource(e.target.value);
                  }}
                >
                  {outputAudioDeviceList?.map((i, index) => (
                    <option key={index} value={i.deviceId!}>
                      {i.label}
                    </option>
                  ))}
                </select>
              </div>,
            ]}
          />
        </Modal>
      )}
      <div className={styles.videos} id="videos">
        <div
          className={`${
            streams.length > 1 ? styles.coupleSubscriber : styles.subscriber
          }`}
          id="subscriber"
        ></div>
        <div
          className={`${
            streams.length > 1 ? styles.couplePublisher : styles.publisher
          }`}
          id="publisher"
        ></div>
      </div>
      <div className={styles.btnWrapper}>
        <div
          className={`${styles.controlBtn} ${
            !showVideo ? styles.offButton : ""
          }`}
          onClick={() => {
            publisher?.publishVideo(!showVideo);
            setShowVideo(!showVideo);
          }}
        >
          {showVideo ? <VideoCameraIcon /> : <VideoCameraSlashIcon />}
        </div>
        <div
          className={`${styles.controlBtn} ${
            !activeAudio ? styles.offButton : ""
          }`}
          onClick={() => {
            publisher?.publishAudio(!activeAudio);
            setActiveAudio(!activeAudio);
          }}
        >
          {activeAudio ? (
            <MicrophoneIcon />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              view-box="0 0 24 24"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 18.75C13.5913 18.75 15.1174 18.1179 16.2426 16.9926C17.3679 15.8674 18 14.3413 18 12.75V11.25M12 18.75C10.4087 18.75 8.88258 18.1179 7.75736 16.9926C6.63214 15.8674 6 14.3413 6 12.75V11.25M12 18.75V22.5M8.25 22.5H15.75M12 15.75C11.2044 15.75 10.4413 15.4339 9.87868 14.8713C9.31607 14.3087 9 13.5456 9 12.75V4.5C9 3.70435 9.31607 2.94129 9.87868 2.37868C10.4413 1.81607 11.2044 1.5 12 1.5C12.7956 1.5 13.5587 1.81607 14.1213 2.37868C14.6839 2.94129 15 3.70435 15 4.5V12.75C15 13.5456 14.6839 14.3087 14.1213 14.8713C13.5587 15.4339 12.7956 15.75 12 15.75Z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 19L17.591 17.591L5.409 5.409L4 4"
                strokeWidth={3.5}
                color="#CB4539"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 19L17.591 17.591L5.409 5.409L4 4"
              />
            </svg>
          )}
        </div>
        {OT.hasMediaProcessorSupport() && (
          <div
            onClick={() => {
              if (publisher?.getVideoFilter() === null) {
                publisher.applyVideoFilter({
                  blurStrength: "high",
                  type: "backgroundBlur",
                });
                setActiveVideoFilter({
                  blurStrength: "high",
                  type: "backgroundBlur",
                });
              } else {
                publisher?.clearVideoFilter();
                setActiveVideoFilter(undefined);
              }
            }}
            className={`${styles.controlBtn} ${
              !activeVideoFilter ? styles.offButton : ""
            }`}
          >
            <ViewfinderCircleIcon />
          </div>
        )}
        <div
          className={styles.controlBtn}
          onClick={() => {
            setSettingModalIsOpen(true);
          }}
        >
          <Cog6ToothIcon />
        </div>
        <div className={styles.endCallButton} onClick={endCall}>
          <PhoneIcon />
        </div>
      </div>
    </div>
  );
};
